import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Shield } from "lucide-react";

const Card = ({ title, accent, children, action }) => (
  <div className="border border-white/10 bg-[var(--noir-elev)]/40 rounded-sm p-5">
    <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/5">
      <div className="font-meta">{title}</div>
      {action != null && (
        <div className="font-fraunces italic text-base text-[var(--cream)]">{accent ?? action}</div>
      )}
    </div>
    {children}
  </div>
);

export default function OnlineSidebar() {
  const { t } = useTranslation();
  const [online, setOnline] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [o, s] = await Promise.all([api.get("/online"), api.get("/stats")]);
        setOnline(o.data); setStats(s.data);
      } catch { /* anon */ }
    };
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  return (
    <aside className="hidden lg:block w-64 shrink-0 space-y-5" data-testid="online-sidebar">
      <Card title={t("sidebar.online_now")} action={online.length}>
        {online.length === 0 ? (
          <div className="font-manrope text-sm text-[var(--cream-faint)] italic">{t("sidebar.no_active")}</div>
        ) : (
          <ul className="space-y-3 max-h-72 overflow-auto pr-1">
            {online.slice(0, 12).map((u) => (
              <li key={u.id}>
                <Link to={`/profile/${u.username}`} className="flex items-center gap-3 group">
                  <span className="relative">
                    <Avatar className="h-7 w-7 rounded-sm border border-white/10">
                      <AvatarFallback className="text-[10px] bg-[var(--magenta)] text-black rounded-sm">
                        {u.username.slice(0,2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="absolute -right-0.5 -bottom-0.5 h-2 w-2 rounded-full bg-[var(--magenta)] online-dot" />
                  </span>
                  <span className="font-fraunces italic text-[var(--cream)] text-base group-hover:text-[var(--magenta)] truncate">
                    {u.username}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {stats && (
        <Card title={t("sidebar.statistics")}>
          <dl className="space-y-2 font-manrope text-sm">
            {[
              [t("sidebar.users"), stats.users], [t("sidebar.threads"), stats.threads],
              [t("sidebar.posts"), stats.posts], [t("sidebar.groups"), stats.groups],
              [t("sidebar.albums"),   stats.albums], [t("sidebar.images"), stats.images],
            ].map(([k, v]) => (
              <div key={k} className="flex items-baseline justify-between">
                <dt className="text-[var(--cream-soft)]">{k}</dt>
                <dd className="font-fraunces italic text-[var(--cream)] text-lg">{v}</dd>
              </div>
            ))}
          </dl>
        </Card>
      )}

      <Card title={t("sidebar.safe_community")}>
        <div className="flex items-start gap-3">
          <Shield className="h-4 w-4 text-[var(--magenta)] shrink-0 mt-0.5" />
          <p className="font-manrope text-xs text-[var(--cream-soft)] leading-relaxed">
            {t("sidebar.safe_community_text")}
          </p>
        </div>
      </Card>
    </aside>
  );
}
